import { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import {
  createTeamsReportRequest,
  getReportQualificationTitlesRequest,
  TeamsReportClientApp,
  TeamsReportSection,
  TeamsReportStat,
  TeamsReportType,
} from '../../../api/reports/teamsReports';
import { getAllTeamMembersRequest } from '../../../api/teams';
import { getStaffRequest } from '../../../api/companies';
import { usePermissions } from '../../../hooks/usePermissions';
import { TEAMS_PERMISSIONS, TeamMember } from '../../../types';
import { TEAMS_RECIPIENT_ROLES } from '../../teams/TeamsSettings/constants';
import { MAX_PEOPLE, MAX_RECIPIENTS, SECTION_PRESETS, SNAPSHOT_STATS, SectionPresetKey } from '../constants';
import {
  buildTeamsReportPayload,
  getMatchingPreset,
  getReportErrorMessage,
  ReportErrorMessage,
  ReportWizardForm,
} from '../utils';
import { useReportStatusPolling } from './useReportStatusPolling';

export type ReportWizardStep =
  | 'type'
  | 'scope'
  | 'select'
  | 'content'
  | 'qualifications'
  | 'analysis'
  | 'review';

export interface RecipientOption {
  uuid: string;
  name: string;
}

export interface UseRequestReportWizardProps {
  open: boolean;
  companyId?: string;
  clientApp: TeamsReportClientApp;
  /** The requester is notified anyway and the gateway refuses them as an extra recipient. */
  currentUserId?: string;
  getStaff?: typeof getStaffRequest;
}

const initialForm = (): ReportWizardForm => ({
  reportType: 'company_snapshot',
  wholeCompany: true,
  teamIds: [],
  stats: SNAPSHOT_STATS.map(stat => stat.value),
  userIds: [],
  // Data minimisation: the least sensitive preset is the starting point
  sections: ['qualifications'],
  allQualifications: true,
  qualificationTitles: [],
  fieldSet: 'lean',
  analysisMode: 'none',
  requestText: '',
  channels: ['email'],
  recipientIds: [],
});

export const useRequestReportWizard = ({
  open,
  companyId,
  clientApp,
  currentUserId,
  getStaff = getStaffRequest,
}: UseRequestReportWizardProps) => {
  const { canAnywhere } = usePermissions();
  const canViewDocuments = canAnywhere(TEAMS_PERMISSIONS.CAN_VIEW_WORKER_DOCUMENTS);
  const canExportPersonal = canAnywhere(TEAMS_PERMISSIONS.CAN_EXPORT_PERSONAL_INFO);

  const [form, setForm] = useState<ReportWizardForm>(() => initialForm());
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<ReportErrorMessage | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  // People search (individual)
  const [peopleSearch, setPeopleSearch] = useState('');
  const [people, setPeople] = useState<TeamMember[]>([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [selectedPeople, setSelectedPeople] = useState<Record<string, TeamMember>>({});

  // Qualification titles held by the selected people
  const [titles, setTitles] = useState<string[]>([]);
  const [titlesLoading, setTitlesLoading] = useState(false);

  // Extra recipients
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientOptions, setRecipientOptions] = useState<RecipientOption[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState<RecipientOption[]>([]);

  const polling = useReportStatusPolling(companyId, requestId);

  const reset = useCallback(() => {
    setForm(initialForm());
    setStepIndex(0);
    setSubmitting(false);
    setSubmitError(null);
    setRequestId(null);
    setPeopleSearch('');
    setSelectedPeople({});
    setTitles([]);
    setSelectedRecipients([]);
    setRecipientSearch('');
  }, []);

  // Each opening starts from scratch; a closed dialog keeps polling nothing
  useEffect(() => {
    if (open) reset();
  }, [open, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback(<K extends keyof ReportWizardForm>(key: K, value: ReportWizardForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setSubmitError(null);
  }, []);

  const steps = useMemo<ReportWizardStep[]>(() => {
    if (form.reportType === 'company_snapshot') return ['type', 'scope', 'analysis', 'review'];
    return [
      'type',
      'select',
      'content',
      ...(form.sections.includes('qualifications') ? (['qualifications'] as ReportWizardStep[]) : []),
      'analysis',
      'review',
    ];
  }, [form.reportType, form.sections]);

  const step = steps[Math.min(stepIndex, steps.length - 1)];

  /* ---------- people ---------- */

  const fetchPeople = useMemo(
    () =>
      debounce(async (substring: string) => {
        if (!companyId) return;
        setPeopleLoading(true);
        try {
          const { data } = await getAllTeamMembersRequest(companyId, {
            offset: 0,
            limit: 50,
            substring,
            order_by: [{ field_name: 'first_name', desc: false }],
            // Every person the caller may report on, not the dashboard's saved filter
            apply_dashboard_filters: false,
          } as any);
          setPeople(data.items || []);
        } catch (e) {
          setPeople([]);
        } finally {
          setPeopleLoading(false);
        }
      }, 400),
    [companyId]
  );

  useEffect(() => {
    if (!open || step !== 'select') return;
    fetchPeople(peopleSearch);
    return () => fetchPeople.cancel();
  }, [open, step, peopleSearch, fetchPeople]);

  const togglePerson = useCallback((person: TeamMember) => {
    setSubmitError(null);
    setSelectedPeople(prev => {
      const next = { ...prev };
      if (next[person.user_id]) delete next[person.user_id];
      else if (Object.keys(next).length < MAX_PEOPLE) next[person.user_id] = person;
      return next;
    });
  }, []);

  useEffect(() => {
    setForm(prev => ({ ...prev, userIds: Object.keys(selectedPeople) }));
  }, [selectedPeople]);

  /* ---------- sections ---------- */

  const isSectionAllowed = useCallback(
    (section: TeamsReportSection) => {
      if (section === 'id_rtw') return canViewDocuments;
      if (section === 'personal') return canExportPersonal;
      return true;
    },
    [canViewDocuments, canExportPersonal]
  );

  const toggleSection = useCallback(
    (section: TeamsReportSection) => {
      if (!isSectionAllowed(section)) return;
      setSubmitError(null);
      setForm(prev => ({
        ...prev,
        sections: prev.sections.includes(section)
          ? prev.sections.filter(item => item !== section)
          : [...prev.sections, section],
      }));
    },
    [isSectionAllowed]
  );

  const applyPreset = useCallback(
    (key: SectionPresetKey) => {
      const preset = SECTION_PRESETS.find(item => item.key === key);
      if (!preset) return;
      setSubmitError(null);
      setForm(prev => ({ ...prev, sections: preset.sections.filter(isSectionAllowed) }));
    },
    [isSectionAllowed]
  );

  const activePreset = getMatchingPreset(form.sections);

  /* ---------- qualification titles ---------- */

  useEffect(() => {
    if (!open || step !== 'qualifications' || !companyId || !form.userIds.length) return;
    let cancelled = false;
    setTitlesLoading(true);
    getReportQualificationTitlesRequest(companyId, { user_ids: form.userIds })
      .then(({ data }) => {
        if (cancelled) return;
        setTitles(data);
        // Drop picks that the current people no longer hold
        setForm(prev => ({
          ...prev,
          qualificationTitles: prev.qualificationTitles.filter(title => data.includes(title)),
        }));
      })
      .catch(() => !cancelled && setTitles([]))
      .finally(() => !cancelled && setTitlesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, step, companyId, form.userIds]);

  const toggleTitle = useCallback((title: string) => {
    setForm(prev => ({
      ...prev,
      qualificationTitles: prev.qualificationTitles.includes(title)
        ? prev.qualificationTitles.filter(item => item !== title)
        : [...prev.qualificationTitles, title],
    }));
  }, []);

  /* ---------- snapshot ---------- */

  const toggleTeam = useCallback((teamId: string) => {
    setSubmitError(null);
    setForm(prev => ({
      ...prev,
      teamIds: prev.teamIds.includes(teamId)
        ? prev.teamIds.filter(id => id !== teamId)
        : [...prev.teamIds, teamId],
    }));
  }, []);

  const toggleStat = useCallback((stat: TeamsReportStat) => {
    setForm(prev => ({
      ...prev,
      stats: prev.stats.includes(stat) ? prev.stats.filter(item => item !== stat) : [...prev.stats, stat],
    }));
  }, []);

  /* ---------- recipients ---------- */

  const fetchRecipients = useMemo(
    () =>
      debounce(async (substring: string) => {
        if (!companyId) return;
        setRecipientsLoading(true);
        try {
          const { data } = await getStaff(companyId, {
            limit: 50,
            offset: 0,
            substring,
            roles: TEAMS_RECIPIENT_ROLES,
          });
          setRecipientOptions(
            data.items
              .filter(item => item.user_id !== currentUserId)
              .map(item => ({ uuid: item.user_id, name: `${item.first_name} ${item.last_name}`.trim() }))
          );
        } catch (e) {
          setRecipientOptions([]);
        } finally {
          setRecipientsLoading(false);
        }
      }, 400),
    [companyId, getStaff, currentUserId]
  );

  useEffect(() => {
    if (!open || step !== 'review' || !form.channels.length) return;
    fetchRecipients(recipientSearch);
    return () => fetchRecipients.cancel();
  }, [open, step, recipientSearch, fetchRecipients, form.channels.length]);

  const changeRecipients = useCallback((recipients: RecipientOption[]) => {
    const next = recipients.slice(0, MAX_RECIPIENTS);
    setSelectedRecipients(next);
    setSubmitError(null);
    setForm(prev => ({ ...prev, recipientIds: next.map(item => item.uuid) }));
  }, []);

  const toggleChannel = useCallback((channel: 'email' | 'messaging') => {
    setSubmitError(null);
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(item => item !== channel)
        : [...prev.channels, channel],
    }));
  }, []);

  /* ---------- navigation ---------- */

  const canGoNext = useMemo(() => {
    switch (step) {
      case 'scope':
        return form.wholeCompany || form.teamIds.length > 0;
      case 'select':
        return form.userIds.length > 0;
      case 'content':
        return form.sections.length > 0;
      case 'qualifications':
        return form.allQualifications || form.qualificationTitles.length > 0;
      case 'analysis':
        return form.analysisMode !== 'custom' || form.requestText.trim().length > 0;
      case 'review':
        return form.reportType === 'individual' || form.stats.length > 0;
      default:
        return true;
    }
  }, [step, form]);

  const goNext = useCallback(() => setStepIndex(index => Math.min(index + 1, steps.length - 1)), [steps]);
  const goBack = useCallback(() => {
    setSubmitError(null);
    setStepIndex(index => Math.max(index - 1, 0));
  }, []);

  const setReportType = useCallback((reportType: TeamsReportType) => update('reportType', reportType), [update]);

  // A full field set needs the personal-info right; fall back to lean if it isn't held
  const fieldSet = canExportPersonal ? form.fieldSet : 'lean';

  const submit = useCallback(async () => {
    if (!companyId || submitting || requestId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = buildTeamsReportPayload({ ...form, fieldSet }, clientApp);
      const { data } = await createTeamsReportRequest(companyId, payload);
      // Kept for good: the Generate button stays disabled, a second click must not order a second report
      setRequestId(data.request_id);
    } catch (e) {
      setSubmitError(getReportErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }, [companyId, submitting, requestId, form, fieldSet, clientApp]);

  /** Back to the review step with every setting kept; Generate then sends a new request. */
  const retry = useCallback(() => {
    setRequestId(null);
    setSubmitError(null);
  }, []);

  return {
    form: { ...form, fieldSet },
    step,
    steps,
    stepIndex,
    update,
    setReportType,
    canGoNext,
    goNext,
    goBack,
    reset,
    // snapshot
    toggleTeam,
    toggleStat,
    // individual
    peopleSearch,
    setPeopleSearch,
    people,
    peopleLoading,
    selectedPeople: Object.values(selectedPeople),
    togglePerson,
    toggleSection,
    applyPreset,
    activePreset,
    isSectionAllowed,
    canViewDocuments,
    canExportPersonal,
    titles,
    titlesLoading,
    toggleTitle,
    // delivery
    toggleChannel,
    recipientOptions,
    recipientsLoading,
    recipientSearch,
    setRecipientSearch,
    selectedRecipients,
    changeRecipients,
    // submit + status
    submit,
    retry,
    submitting,
    submitError,
    requestId,
    polling,
  };
};

export type RequestReportWizardApi = ReturnType<typeof useRequestReportWizard>;
